import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { ApiError, api, uploadPhoto } from '../api/client.js'
import type { ObjectContext, Template } from '../api/types.js'
import { Icon } from '../components/icons.js'
import { ObjectPicker } from '../components/objects.js'
import { useToast } from '../components/toast.js'
import { EmptyState, Field, Modal, Notice, Spinner } from '../components/ui.js'
import { compressImage } from '../lib/camera.js'
import { formatDateTime } from '../lib/format.js'

type Rating = 'ok' | 'maengel' | 'nicht_ok' | 'na'

interface Checklist {
  id: string
  name: string
  templateId: string | null
  templateName: string | null
  items: { id: string; label: string; weight: number }[]
}

interface InspectionRow extends ObjectContext {
  id: string
  checklistName: string
  inspectedAt: string
  inspectorName: string | null
  score: number
  passed: boolean
  fromSample: boolean
  note: string | null
  failed: number
  minor: number
}

const RATINGS: { value: Rating; label: string; short: string }[] = [
  { value: 'ok', label: 'In Ordnung', short: 'OK' },
  { value: 'maengel', label: 'Kleine Mängel', short: 'Mängel' },
  { value: 'nicht_ok', label: 'Nicht in Ordnung', short: 'Nicht OK' },
  { value: 'na', label: 'Entfällt', short: 'Entfällt' },
]
const WEIGHT_LABEL: Record<number, string> = { 1: 'normal', 2: 'wichtig', 3: 'sehr wichtig' }

/** Same rule as on the server: in order counts fully, minor defects half, "entfällt" not at all. */
function score(lines: { weight: number; rating?: Rating }[]): number | null {
  const counted = lines.filter((l) => l.rating && l.rating !== 'na')
  if (counted.length === 0) return null
  const total = counted.reduce((s, l) => s + l.weight, 0)
  const got = counted.reduce((s, l) => s + l.weight * (l.rating === 'ok' ? 1 : l.rating === 'maengel' ? 0.5 : 0), 0)
  return Math.round((got / total) * 100)
}

export function ScoreBadge({ value, passed }: { value: number; passed: boolean }) {
  return <span className={`score-badge ${passed ? 'good' : 'bad'}`}>{value} %</span>
}

/**
 * Qualitätsbegehungen on the Qualität page: the list, the random sample and
 * the checklists. The score is about the object on that day.
 */
export function InspectionsPanel() {
  const [days, setDays] = useState(90)
  const [start, setStart] = useState<{ objectId: string; fromSample: boolean } | null>(null)
  const [showChecklists, setShowChecklists] = useState(false)
  const [showSample, setShowSample] = useState(false)
  const inspections = useQuery({
    queryKey: ['inspections', days],
    queryFn: () => api<InspectionRow[]>(`/api/quality/inspections?days=${days}`),
  })
  const checklists = useQuery({ queryKey: ['checklists'], queryFn: () => api<Checklist[]>('/api/quality/checklists') })
  const list = inspections.data ?? []
  const avg = list.length ? Math.round(list.reduce((s, i) => s + i.score, 0) / list.length) : null
  const passedShare = list.length ? Math.round((list.filter((i) => i.passed).length / list.length) * 100) : null

  return (
    <section className="card" style={{ marginBottom: 18 }}>
      <div className="card-head">
        <div>
          <h2>
            <Icon name="check" size={16} /> Qualitätsbegehungen
          </h2>
          <div className="muted small">
            Mit Checkliste vor Ort prüfen, wichtige Punkte zählen mehr. Bewertet wird der Zustand des Objekts, nicht die Person.
          </div>
        </div>
        <div className="actions">
          <select className="select-inline" value={days} onChange={(e) => setDays(Number(e.target.value))} aria-label="Zeitraum">
            <option value={30}>30 Tage</option>
            <option value={90}>90 Tage</option>
            <option value={365}>12 Monate</option>
          </select>
          <button type="button" className="btn secondary" onClick={() => setShowChecklists(true)}>
            Checklisten ({checklists.data?.length ?? 0})
          </button>
          <button type="button" className="btn secondary" onClick={() => setShowSample(true)} disabled={!checklists.data?.length}>
            Stichprobe ziehen
          </button>
          <button type="button" className="btn" onClick={() => setStart({ objectId: '', fromSample: false })} disabled={!checklists.data?.length}>
            <Icon name="plus" /> Neue Begehung
          </button>
        </div>
      </div>

      {checklists.data && checklists.data.length === 0 ? (
        <EmptyState
          title="Noch keine Checkliste"
          hint="Zuerst festlegen, was geprüft wird und wie wichtig es ist, z. B. für die Treppenhausreinigung."
          action={
            <button type="button" className="btn" onClick={() => setShowChecklists(true)}>
              Checkliste anlegen
            </button>
          }
        />
      ) : inspections.isLoading ? (
        <Spinner />
      ) : list.length === 0 ? (
        <EmptyState title="Noch keine Begehungen im Zeitraum" hint="Über „Stichprobe ziehen“ wählt Hauswerk zufällig Objekte, die kürzlich gereinigt wurden." />
      ) : (
        <>
          <div className="inspection-summary">
            <span>
              <strong>{list.length}</strong> Begehungen
            </span>
            {avg !== null && (
              <span>
                Durchschnitt <strong>{avg} %</strong>
              </span>
            )}
            {passedShare !== null && (
              <span>
                bestanden <strong>{passedShare} %</strong>
              </span>
            )}
          </div>
          <div className="table-scroll">
            <table className="data">
              <thead>
                <tr>
                  <th>Datum</th>
                  <th>Objekt</th>
                  <th>Checkliste</th>
                  <th className="nowrap">Ergebnis</th>
                  <th>Auffällig</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {list.map((i) => (
                  <tr key={i.id}>
                    <td className="nowrap small">
                      {formatDateTime(i.inspectedAt)}
                      {i.fromSample && <div className="muted">Stichprobe</div>}
                    </td>
                    <td>
                      <Link to={`/objekte/${i.objectId}`}>{i.siteName}</Link>
                      {i.path.length > 0 && <div className="muted small">{i.path.join(' › ')}</div>}
                    </td>
                    <td className="small">
                      {i.checklistName}
                      {i.inspectorName && <div className="muted">{i.inspectorName}</div>}
                    </td>
                    <td className="nowrap">
                      <ScoreBadge value={i.score} passed={i.passed} /> <span className="muted small">{i.passed ? 'bestanden' : 'nicht bestanden'}</span>
                    </td>
                    <td className="small">
                      {i.failed + i.minor === 0 ? <span className="muted">nichts</span> : `${i.failed} nicht OK, ${i.minor} kleine Mängel`}
                    </td>
                    <td className="nowrap" style={{ textAlign: 'right' }}>
                      <a className="btn ghost small" href={`/api/quality/inspections/${i.id}/report`} target="_blank" rel="noreferrer">
                        Prüfbericht
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {start && (
        <InspectionModal
          checklists={checklists.data ?? []}
          initialObjectId={start.objectId}
          fromSample={start.fromSample}
          onClose={() => setStart(null)}
        />
      )}
      {showChecklists && <ChecklistsModal checklists={checklists.data ?? []} onClose={() => setShowChecklists(false)} />}
      {showSample && (
        <SampleModal
          onClose={() => setShowSample(false)}
          onStart={(objectId) => {
            setShowSample(false)
            setStart({ objectId, fromSample: true })
          }}
        />
      )}
    </section>
  )
}

function InspectionModal({
  checklists,
  initialObjectId,
  fromSample,
  onClose,
}: {
  checklists: Checklist[]
  initialObjectId: string
  fromSample: boolean
  onClose: () => void
}) {
  const queryClient = useQueryClient()
  const toast = useToast()
  const [objectId, setObjectId] = useState(initialObjectId)
  const [checklistId, setChecklistId] = useState(checklists[0]?.id ?? '')
  const [ratings, setRatings] = useState<Record<string, Rating>>({})
  const [notes, setNotes] = useState<Record<string, string>>({})
  const [createIssue, setCreateIssue] = useState<Record<string, boolean>>({})
  const [note, setNote] = useState('')
  const [photos, setPhotos] = useState<{ blob: Blob; previewUrl: string }[]>([])
  const [error, setError] = useState<string | null>(null)
  const checklist = checklists.find((c) => c.id === checklistId)
  const current = useMemo(() => score((checklist?.items ?? []).map((i) => ({ weight: i.weight, rating: ratings[i.id] }))), [checklist, ratings])
  const open = (checklist?.items ?? []).filter((i) => !ratings[i.id]).length

  const save = useMutation({
    mutationFn: async () => {
      const photoIds: string[] = []
      for (const p of photos) photoIds.push((await uploadPhoto(p.blob, 'inspection')).id)
      return api<{ score: number; passed: boolean; issuesCreated: number }>('/api/quality/inspections', {
        method: 'POST',
        body: {
          objectId,
          checklistId,
          fromSample,
          note: note || undefined,
          photoIds,
          results: checklist!.items.map((i) => ({
            itemId: i.id,
            rating: ratings[i.id],
            note: notes[i.id] || undefined,
            createIssue: Boolean(createIssue[i.id]),
          })),
        },
      })
    },
    onSuccess: async (result) => {
      toast(`Begehung gespeichert: ${result.score} %, ${result.passed ? 'bestanden' : 'nicht bestanden'}${result.issuesCreated ? `, ${result.issuesCreated} Mängel angelegt` : ''}.`)
      await queryClient.invalidateQueries({ queryKey: ['inspections'] })
      await queryClient.invalidateQueries({ queryKey: ['issues'] })
      onClose()
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Speichern fehlgeschlagen.'),
  })

  function rate(itemId: string, rating: Rating) {
    setRatings({ ...ratings, [itemId]: rating })
    // Not in order is usually worth a Mangel; minor defects rarely are.
    if (rating === 'nicht_ok' && createIssue[itemId] === undefined) setCreateIssue({ ...createIssue, [itemId]: true })
  }

  return (
    <Modal
      open
      title={fromSample ? 'Begehung aus der Stichprobe' : 'Neue Begehung'}
      onClose={onClose}
      footer={
        <>
          <span className="grow muted small">{open > 0 ? `Noch ${open} ${open === 1 ? 'Punkt' : 'Punkte'} offen` : 'Alle Punkte bewertet'}</span>
          <button type="button" className="btn secondary" onClick={onClose}>
            Abbrechen
          </button>
          <button type="button" className="btn" disabled={!objectId || !checklist || open > 0 || save.isPending} onClick={() => save.mutate()}>
            {save.isPending ? 'Wird gespeichert …' : 'Begehung speichern'}
          </button>
        </>
      }
    >
      {error && <Notice kind="error">{error}</Notice>}
      <div className="row">
        <Field label="Objekt">
          <ObjectPicker value={objectId} onChange={(id) => setObjectId(id)} placeholder="Liegenschaft, Gebäude oder Einheit" />
        </Field>
        <Field label="Checkliste">
          <select value={checklistId} onChange={(e) => { setChecklistId(e.target.value); setRatings({}) }}>
            {checklists.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </Field>
      </div>

      <div className="inspection-live">
        <span>Ergebnis bisher</span>
        <strong className={current === null ? '' : current >= 80 ? 'good' : 'bad'}>{current === null ? '–' : `${current} %`}</strong>
      </div>

      <ol className="inspection-items">
        {checklist?.items.map((item) => (
          <li key={item.id} className={ratings[item.id] ?? ''}>
            <div className="item-head">
              <span className="strong">{item.label}</span>
              <span className={`weight w${item.weight}`}>{WEIGHT_LABEL[item.weight]}</span>
            </div>
            <div className="rating-buttons" role="radiogroup" aria-label={item.label}>
              {RATINGS.map((r) => (
                <button key={r.value} type="button" role="radio" aria-checked={ratings[item.id] === r.value} className={`rating ${r.value} ${ratings[item.id] === r.value ? 'on' : ''}`} onClick={() => rate(item.id, r.value)}>
                  {r.label}
                </button>
              ))}
            </div>
            {(ratings[item.id] === 'maengel' || ratings[item.id] === 'nicht_ok') && (
              <div className="item-extra">
                <input placeholder="Was genau? z. B. Ecke 2. OG" value={notes[item.id] ?? ''} onChange={(e) => setNotes({ ...notes, [item.id]: e.target.value })} />
                <label className="switch-inline">
                  <input type="checkbox" checked={Boolean(createIssue[item.id])} onChange={(e) => setCreateIssue({ ...createIssue, [item.id]: e.target.checked })} />
                  als Mangel anlegen
                </label>
              </div>
            )}
          </li>
        ))}
      </ol>

      <Field label="Gesamteindruck (freiwillig)">
        <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} />
      </Field>
      <Field label="Fotos (freiwillig)">
        <input
          type="file"
          accept="image/*"
          multiple
          onChange={async (e) => {
            const files = [...(e.target.files ?? [])].slice(0, 6)
            const shots = await Promise.all(files.map((f) => compressImage(f)))
            setPhotos(shots.map((s) => ({ blob: s.blob, previewUrl: s.previewUrl })))
          }}
        />
      </Field>
      {photos.length > 0 && (
        <div className="photo-grid">
          {photos.map((p, i) => (
            <img key={i} src={p.previewUrl} alt="" />
          ))}
        </div>
      )}
    </Modal>
  )
}

function ChecklistsModal({ checklists, onClose }: { checklists: Checklist[]; onClose: () => void }) {
  const [editing, setEditing] = useState<Checklist | 'new' | null>(checklists.length === 0 ? 'new' : null)
  if (editing) return <ChecklistEditor checklist={editing === 'new' ? null : editing} onClose={() => (checklists.length === 0 ? onClose() : setEditing(null))} />
  return (
    <Modal
      open
      title="Checklisten"
      onClose={onClose}
      footer={
        <button type="button" className="btn" onClick={() => setEditing('new')}>
          <Icon name="plus" /> Neue Checkliste
        </button>
      }
    >
      <ul className="feed">
        {checklists.map((c) => (
          <li key={c.id}>
            <div className="grow">
              <div className="strong">{c.name}</div>
              <div className="muted small">
                {c.items.length} Prüfpunkte{c.templateName ? `, für ${c.templateName}` : ''}
              </div>
            </div>
            <button type="button" className="btn secondary small" onClick={() => setEditing(c)}>
              Bearbeiten
            </button>
          </li>
        ))}
      </ul>
    </Modal>
  )
}

function ChecklistEditor({ checklist, onClose }: { checklist: Checklist | null; onClose: () => void }) {
  const queryClient = useQueryClient()
  const toast = useToast()
  const templates = useQuery({ queryKey: ['templates'], queryFn: () => api<Template[]>('/api/templates') })
  const [name, setName] = useState(checklist?.name ?? '')
  const [templateId, setTemplateId] = useState(checklist?.templateId ?? '')
  const [items, setItems] = useState<{ id?: string; label: string; weight: number }[]>(
    checklist?.items ?? [
      { label: 'Boden gewischt', weight: 3 },
      { label: 'Handläufe und Geländer', weight: 2 },
      { label: 'Fensterbänke', weight: 1 },
      { label: 'Spinnweben', weight: 1 },
    ],
  )
  const [error, setError] = useState<string | null>(null)
  const save = useMutation({
    mutationFn: () => {
      const body = { name, templateId: templateId || null, items: items.filter((i) => i.label.trim()) }
      return checklist ? api(`/api/quality/checklists/${checklist.id}`, { method: 'PATCH', body }) : api('/api/quality/checklists', { method: 'POST', body })
    },
    onSuccess: async () => {
      toast('Checkliste gespeichert.')
      await queryClient.invalidateQueries({ queryKey: ['checklists'] })
      onClose()
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Speichern fehlgeschlagen.'),
  })
  return (
    <Modal
      open
      title={checklist ? `Checkliste ${checklist.name}` : 'Neue Checkliste'}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn secondary" onClick={onClose}>
            Abbrechen
          </button>
          <button type="button" className="btn" disabled={!name.trim() || save.isPending} onClick={() => save.mutate()}>
            Speichern
          </button>
        </>
      }
    >
      {error && <Notice kind="error">{error}</Notice>}
      <div className="row">
        <Field label="Name">
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="z. B. Treppenhausreinigung" autoFocus />
        </Field>
        <Field label="Zur Leistung (freiwillig)">
          <select value={templateId} onChange={(e) => setTemplateId(e.target.value)}>
            <option value="">keine</option>
            {templates.data?.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
        </Field>
      </div>
      <div className="field">
        <span>Prüfpunkte und Gewicht</span>
        <div className="hint" style={{ marginTop: 0 }}>
          Sehr wichtig zählt dreifach, wichtig doppelt. Ein sauberer Boden wiegt so mehr als Spinnweben.
        </div>
      </div>
      <ul className="checklist-edit">
        {items.map((item, index) => (
          <li key={index}>
            <input value={item.label} onChange={(e) => setItems(items.map((it, i) => (i === index ? { ...it, label: e.target.value } : it)))} placeholder="Prüfpunkt" />
            <select value={item.weight} onChange={(e) => setItems(items.map((it, i) => (i === index ? { ...it, weight: Number(e.target.value) } : it)))}>
              <option value={1}>normal</option>
              <option value={2}>wichtig</option>
              <option value={3}>sehr wichtig</option>
            </select>
            <button type="button" className="btn ghost small" aria-label="Entfernen" onClick={() => setItems(items.filter((_, i) => i !== index))}>
              <Icon name="close" size={14} />
            </button>
          </li>
        ))}
      </ul>
      <button type="button" className="btn secondary small" onClick={() => setItems([...items, { label: '', weight: 1 }])}>
        <Icon name="plus" size={14} /> Prüfpunkt
      </button>
    </Modal>
  )
}

function SampleModal({ onClose, onStart }: { onClose: () => void; onStart: (objectId: string) => void }) {
  const [count, setCount] = useState(5)
  const [objectId, setObjectId] = useState('')
  const [seed, setSeed] = useState(0)
  const sample = useQuery({
    queryKey: ['quality-sample', count, objectId, seed],
    queryFn: () => api<{ poolSize: number; sample: ObjectContext[] }>(`/api/quality/sample?count=${count}${objectId ? `&objectId=${objectId}` : ''}`),
  })
  return (
    <Modal
      open
      title="Zufallsstichprobe"
      onClose={onClose}
      footer={
        <button type="button" className="btn secondary" onClick={() => setSeed(seed + 1)}>
          Neu ziehen
        </button>
      }
    >
      <p className="small muted" style={{ marginTop: 0 }}>
        Objekte, die in den letzten 14 Tagen gereinigt und seit 30 Tagen nicht begangen wurden, zufällig gezogen. So prüft niemand nur dort,
        wo es ohnehin gut aussieht.
      </p>
      <div className="row">
        <Field label="Eingrenzen (freiwillig)">
          <ObjectPicker value={objectId} onChange={(id) => setObjectId(id)} taskable={false} placeholder="Alle Kunden" />
        </Field>
        <Field label="Anzahl">
          <select value={count} onChange={(e) => setCount(Number(e.target.value))}>
            {[3, 5, 10, 15].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </Field>
      </div>
      {sample.isLoading ? (
        <Spinner />
      ) : !sample.data || sample.data.sample.length === 0 ? (
        <EmptyState title="Nichts zu ziehen" hint="Keine kürzlich gereinigten Objekte ohne Begehung in den letzten 30 Tagen." />
      ) : (
        <ul className="feed">
          {sample.data.sample.map((s) => (
            <li key={s.objectId}>
              <div className="grow">
                <div className="strong">{s.siteName}</div>
                <div className="muted small">{s.path.length ? s.path.join(' › ') : 'gesamte Liegenschaft'}</div>
              </div>
              <button type="button" className="btn small" onClick={() => onStart(s.objectId)}>
                Begehung starten
              </button>
            </li>
          ))}
          <li className="muted small">aus {sample.data.poolSize} möglichen Objekten</li>
        </ul>
      )}
    </Modal>
  )
}
